import { ChevronDown, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useOpenVerificationEvidence } from "../license-verification/use-open-verification-evidence";
import { VerificationEvidencePreview } from "../verification-evidence-preview/verification-evidence-preview";
import { RegistryReportDetails } from "../registry-report";
import { VerificationSourceLink } from "../verification-source-link";
import type { RunReport, RunScope } from "./types";

// The API numbers each Medicare opt-out record's fields ("Affidavit 1 ·
// Name"); the item's banner lists the records as their own blocks instead.
const MEDICARE_RECORD_DETAIL_PREFIX = "Affidavit ";

type AssessmentItem =
  RunReport["checks"][number]["assessment"]["items"][number];

/** The item's report without the per-record details the banner lists. */
function withoutMedicareRecordDetails(item: AssessmentItem): AssessmentItem {
  return {
    ...item,
    details: item.details.filter(
      (detail) => !detail.label.startsWith(MEDICARE_RECORD_DETAIL_PREFIX),
    ),
  };
}

/** Each result's report, or only `resultKey`'s for an item that covers one
 * result (one DEA registration). */
export function RegistryCheckResults({
  check,
  runId,
  scope,
  resultKey,
}: {
  check: RunReport["checks"][number];
  runId: string;
  scope: RunScope;
  resultKey?: string;
}) {
  const isMedicare = check.family === "medicare-participation";
  const items = resultKey
    ? check.assessment.items.filter((item) => item.resultKey === resultKey)
    : check.assessment.items;
  const evidencePreview = useOpenVerificationEvidence({
    scope,
    runId,
    checkId: check.id,
  });
  return (
    <section
      aria-label={`${check.authorityName} details`}
      className="space-y-4"
    >
      {items.map((item) => {
        const result = check.results?.find(
          (result) => result.resultKey === item.resultKey,
        );
        const evidence = check.evidenceManifest.find(
          (file) =>
            file.kind === "source-data" &&
            file.mediaType === "application/json" &&
            file.key === result?.facts.sourceDataEvidenceKey &&
            result.evidenceKeys.includes(file.key),
        );
        return (
          <section
            key={item.resultKey}
            aria-label={item.title}
            className="space-y-4"
          >
            {items.length > 1 && (
              <h5 className="text-sm font-semibold">{item.title}</h5>
            )}
            <RegistryReportDetails
              showVerdict={false}
              report={isMedicare ? withoutMedicareRecordDetails(item) : item}
            />
            <div className="flex flex-wrap items-center gap-3">
              <VerificationSourceLink sourceUrl={result?.facts.sourceUrl} />
              {evidence && (
                <Button
                  variant="surface"
                  size="sm"
                  onClick={() =>
                    evidencePreview.open({
                      fileUploadId: evidence.fileUploadId,
                      label: `${item.title} source data`,
                    })
                  }
                >
                  <Eye className="mr-2 size-4" aria-hidden="true" />
                  View source data
                </Button>
              )}
            </div>
          </section>
        );
      })}
      {isMedicare && check.snapshotId && (
        <Collapsible className="space-y-2 border-t pt-2">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="group h-auto gap-1 p-0 text-xs text-muted-foreground"
            >
              Audit records
              <ChevronDown
                className="size-3 transition-transform group-data-[state=open]:rotate-180"
                aria-hidden="true"
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              The full CMS source dataset used for this check is retained for
              audit or export.
            </p>
            <Button
              type="button"
              variant="surface"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                evidencePreview.open({
                  snapshot: true,
                  label: "CMS Medicare opt-out dataset",
                })
              }
            >
              <Eye className="size-4" aria-hidden="true" />
              CMS Medicare opt-out dataset
            </Button>
          </CollapsibleContent>
        </Collapsible>
      )}
      <VerificationEvidencePreview {...evidencePreview} />
    </section>
  );
}
